import {
  ChatBubbleLeftEllipsisIcon,
  LinkIcon
} from '@heroicons/react/24/outline'
import { motion } from 'framer-motion'
import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { colors } from '../../constants/colors'
import { useNotificationContext } from '../../context'
import { getInviteToAssessmentLink } from '../../helpers'
import { useCopyToClipboard } from '../../hooks/use-copy-to-clipboard'
import { AnimatedCopyIcon, EASE } from '../motion'
import Button from '../tailwind/Button'

const I18N = 'components.career.invite_talent_modal.'

// gray-50, Grundfarbe des Linkfelds
const FIELD_BACKGROUND = '#F9FAFB'

// Einladungslink eines Talents zum Kopieren. Mit message gibt es zusätzlich
// „Mit Nachricht kopieren“ für den Versand über eigene Kanäle.
const SpaceInviteLink = ({ spaceID, language, message, autoFocus }) => {
  const { t } = useTranslation()
  const { error } = useNotificationContext()
  const [announcement, setAnnouncement] = useState('')

  const linkCopy = useCopyToClipboard()
  const messageCopy = useCopyToClipboard()

  const inviteLink = useMemo(
    () => getInviteToAssessmentLink({ space_id: spaceID, language }),
    [spaceID, language]
  )

  const copy = async ({ copyFn, text, announcementKey }) => {
    const copied = await copyFn(text)

    if (!copied) {
      error(t(I18N + 'copy_error'))
      return
    }

    setAnnouncement(t(I18N + announcementKey))
  }

  const selectAll = (event) => event.target.select()

  return (
    <div>
      <label htmlFor={`invite-link-${spaceID}`} className='sr-only'>
        {t(I18N + 'link_field_label')}
      </label>
      <motion.div
        className='flex items-center gap-x-2 rounded-lg px-3 py-2.5 ring-1 ring-inset ring-gray-200 focus-within:ring-2 focus-within:ring-primaryBlue'
        style={{ backgroundColor: FIELD_BACKGROUND }}
        animate={
          linkCopy.copied || messageCopy.copied
            ? { backgroundColor: [colors.paleBlue, FIELD_BACKGROUND] }
            : undefined
        }
        transition={{ duration: 0.8, ease: EASE.out }}
      >
        <LinkIcon
          className='size-4 shrink-0 text-gray-500'
          aria-hidden='true'
        />
        <input
          id={`invite-link-${spaceID}`}
          type='text'
          readOnly
          value={inviteLink}
          onFocus={selectAll}
          onClick={selectAll}
          className='font-mono w-full min-w-0 truncate border-0 bg-transparent p-0 text-xs text-gray-900 focus:ring-0'
        />
      </motion.div>
      <div className='mt-3 flex flex-wrap gap-2'>
        <Button.PrimaryLG
          autoFocus={autoFocus}
          minWidth='min-w-36'
          icon={
            <AnimatedCopyIcon
              copied={linkCopy.copied}
              checkClassName='text-white'
            />
          }
          text={t(
            I18N + (linkCopy.copied ? 'copy_link_success' : 'copy_link_action')
          )}
          onClick={() =>
            copy({
              copyFn: linkCopy.copy,
              text: inviteLink,
              announcementKey: 'copy_link_announcement'
            })
          }
        />
        {message && (
          <Button.SecondaryLG
            icon={
              <AnimatedCopyIcon
                copied={messageCopy.copied}
                icon={ChatBubbleLeftEllipsisIcon}
              />
            }
            text={t(
              I18N +
                (messageCopy.copied
                  ? 'copy_message_success'
                  : 'copy_message_action')
            )}
            onClick={() =>
              copy({
                copyFn: messageCopy.copy,
                text: message,
                announcementKey: 'copy_message_announcement'
              })
            }
          />
        )}
      </div>
      <p className='mt-3 text-xs text-gray-500'>
        {t(I18N + 'link_findable_hint')}
      </p>
      <span className='sr-only' aria-live='polite'>
        {announcement}
      </span>
    </div>
  )
}

SpaceInviteLink.propTypes = {
  spaceID: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  message: PropTypes.string,
  autoFocus: PropTypes.bool
}

SpaceInviteLink.defaultProps = {
  message: undefined,
  autoFocus: false
}

export default SpaceInviteLink
